// OCViewer — Vista HTML de la OC que refleja 1:1 el PDF generado por
// backend/pdf_generators.py::generate_oc_pdf. Se usa en el modal
// "Aceptación/Rechazo de DTE" (pages/dte/modals/OcModal.jsx) y en cualquier
// otra pantalla que quiera mostrar la OC sin descargar el PDF.
//
// Iter 290.d (Carlos, feb-2026) — Alineación total con el PDF:
//   • Cuadro superior derecho: siempre "ORDEN DE COMPRA" (nunca "OC EXPRESS")
//     y sin la línea "OT: …" (esa OT ya viaja en la columna OT de cada ítem).
//   • Fila azul superior a la tabla: "CONDICION DE PAGO" + "MEDIO DE PAGO"
//     (dos columnas) igual que el PDF.
//   • Elimina la sección duplicada "FORMA DE PAGO" en el bloque inferior.
//   • Totales: añade línea "EXENTO" cuando corresponde, "NETO AFECTO" en
//     lugar de "VALOR NETO" al tener exento, más otros_impuestos y retención.
//   • Firmas: cuadros/sellos sin rotación, matcheando la disposición del PDF.
//   • Elimina la sección "Operaciones Realizadas" (no aparece en el PDF).
//   • Agrega footer "Generado por: … · Generado el DD-MM-YYYY HH:mm · CHIRINO STEEL".
import { useState, useEffect } from 'react';
import { AlertTriangle, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { fmtFecha } from '../lib/utils';
import api from '../lib/api';
import { SelloRecepcionBox } from './SelloRecepcionBox';
import { VbPagoBox } from './VbPagoBox';

const LOGO_URL = 'https://static.prod-images.emergentagent.com/jobs/c2336676-31cc-474c-9251-ec38b5d77ca2/images/44081c023cd8620228825e9431f0f4164cc08ec753d5ac6153ea0a793b40effd.png';

const fmt = (n) => {
  if (n === undefined || n === null || n === '' || isNaN(n)) return '0';
  return Math.round(Number(n)).toLocaleString('es-CL');
};

const fmtFechaHora = (v) => {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}-${mm}-${yy} ${hh}:${mi}`;
  } catch {
    return String(v);
  }
};

export function OCViewer({ oc, detalle = [], proveedor = {}, operaciones = [], formaPago = '', empresa = {}, editableSello = false, editableVbPago = false, onVbPagoChange }) {
  // Iter 411 — Sello de Recepción: se carga por sí mismo para que todas las
  // pantallas que usan OCViewer lo muestren sin cambios adicionales.
  const [sello, setSello] = useState(null);
  // Iter 413 — V°B° para Pago: igual que el sello, se autocarga.
  const [vbPago, setVbPago] = useState(null);
  const numeroOc = oc?.numero_oc;
  useEffect(() => {
    let cancel = false;
    if (!numeroOc) { setSello(null); return undefined; }
    api.get(`/bodega/oc/${numeroOc}/sello-recepcion`)
      .then((res) => { if (!cancel) setSello(res.data); })
      .catch(() => { if (!cancel) setSello(null); });
    return () => { cancel = true; };
  }, [numeroOc]);
  useEffect(() => {
    let cancel = false;
    if (!numeroOc) { setVbPago(null); return undefined; }
    api.get(`/compras/oc/${numeroOc}/vb-pago`)
      .then((res) => { if (!cancel) setVbPago(res.data); })
      .catch(() => { if (!cancel) setVbPago(null); });
    return () => { cancel = true; };
  }, [numeroOc]);

  if (!oc) return null;
  const neto = oc.neto || 0;
  const iva = oc.iva || 0;
  const total = oc.total || 0;
  const netoExento = Number(oc.neto_exento || 0);
  const netoAfecto = neto - netoExento;
  const otrosImpuestos = Array.isArray(oc.otros_impuestos) ? oc.otros_impuestos : [];
  const retencionMonto = Number(oc.retencion_monto || 0);
  const retencionPct = oc.retencion_porcentaje || 0;
  const ivaPct = oc.iva_porcentaje || 19;

  const condPago = (formaPago || oc.condicion_pago || '').toString();
  const medioPagoNombre = (oc.medio_pago_nombre || '').toString();

  const co = {
    razon_social: empresa.razon_social || 'CHIRINO STEEL ING. Y CONT. SPA',
    rut: empresa.rut || '76.738.421-1',
    direccion: empresa.direccion || 'RUTA 5 SUR KM 1032, PARCELA 2',
    giro: empresa.giro || 'INGENIERIA Y CONSTRUCCION',
    slogan: empresa.slogan || 'Ingenieria - Fabricacion - Montaje',
  };

  // Nombre del que emite (Adquisiciones) — misma prioridad que el PDF.
  const expressOp = operaciones.find((op) => op.tipo === 'OC Express Creada');
  const creadorNombre = oc.creado_por_nombre
    || expressOp?.nombre
    || (oc.creado_por ? oc.creado_por.split('@')[0].replace(/[._]/g, ' ') : '');

  // Nombre del que da V.B. Gerencia
  const vbOp = operaciones.find((op) => op.tipo === 'VB Gerencia');
  let gerenteNombre = '';
  if (oc.vb_gerencia_por) {
    if (oc.vb_gerencia_por === oc.creado_por && oc.solicitante) {
      gerenteNombre = oc.solicitante;
    } else if (vbOp?.nombre) {
      gerenteNombre = vbOp.nombre;
    } else if (oc.vb_gerencia_por.includes && oc.vb_gerencia_por.includes('@')) {
      gerenteNombre = oc.vb_gerencia_por.split('@')[0].replace(/[._]/g, ' ');
    } else {
      gerenteNombre = oc.vb_gerencia_por;
    }
  }

  const generadoPor = expressOp?.nombre || oc.creado_por_nombre || creadorNombre || '';
  const generadoEl = oc.fecha_creacion || expressOp?.fecha || oc.fecha_norm || oc.fecha;

  // Iter 292.a (Carlos, feb-2026): botón Imprimir la OC usando el PDF real
  // generado por backend/pdf_generators.py::generate_oc_pdf — mismo formato
  // que "Genera OC" para mantener consistencia visual entre reportes.
  // Descarga el blob y lo imprime en un iframe oculto (sin abrir pestaña).
  const handlePrint = async () => {
    const numero = oc?.numero_oc;
    if (!numero) {
      toast.error('OC sin número — no se puede imprimir');
      return;
    }
    try {
      const res = await api.get(`/compras/oc/${numero}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch {
          window.open(url, '_blank');
        }
        // Cleanup tras 60s (tiempo suficiente para el diálogo de impresión)
        setTimeout(() => {
          try { URL.revokeObjectURL(url); } catch { /* noop */ }
          try { document.body.removeChild(iframe); } catch { /* noop */ }
        }, 60000);
      };
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo obtener el PDF de la OC');
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
          title="Imprimir esta Orden de Compra (tamaño Carta)"
          data-testid="btn-imprimir-oc"
        >
          <Printer className="w-3.5 h-3.5" /> Imprimir (Carta)
        </button>
      </div>
    <div className="bg-white text-[11px] leading-tight font-sans border border-gray-300" data-testid="oc-viewer">

      {/* === HEADER: Logo + Company Info + Blue Box === */}
      <div className="flex items-start border-b-2 border-[#003366]">
        <div className="flex-1 p-3">
          <div className="flex items-center gap-3 mb-2">
            <img src={LOGO_URL} alt="CHIRINO STEEL" className="h-12 w-auto object-contain" />
            <div>
              <p className="text-[13px] font-black text-[#003366] tracking-tight">{co.razon_social}</p>
              <p className="text-[8px] text-gray-500 uppercase tracking-wider">{co.slogan}</p>
            </div>
          </div>
          <table className="text-[9px] text-gray-600">
            <tbody>
              <tr>
                <td className="font-bold pr-2 text-[#003366]">RAZON SOCIAL:</td>
                <td>{co.razon_social}</td>
              </tr>
              <tr>
                <td className="font-bold pr-2 text-[#003366]">DIRECCION:</td>
                <td>{co.direccion}</td>
              </tr>
              <tr>
                <td className="font-bold pr-2 text-[#003366]">GIRO:</td>
                <td>{co.giro}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Right: Blue box — SIEMPRE "ORDEN DE COMPRA" (igual que el PDF).
            La OT ya viaja en la columna OT de cada ítem, no la duplicamos aquí. */}
        <div className="w-[220px] m-3 border-2 border-[#003366] rounded p-3 flex flex-col items-center bg-[#f0f4f8]" data-testid="oc-viewer-header-box">
          <p className="text-[10px] font-bold text-[#003366]">R.U.T.: {co.rut}</p>
          <p className="text-[13px] font-black text-[#003366] mt-1.5 text-center">
            ORDEN DE COMPRA
          </p>
          <p className="text-[16px] font-black text-[#003366] mt-0.5">N {oc.numero_oc}</p>
        </div>
      </div>

      {/* === PROVEEDOR + FECHA === */}
      <div className="border-b border-[#003366] p-3">
        <div className="flex gap-6">
          <div className="flex-1">
            <table className="text-[9px]">
              <tbody>
                <tr>
                  <td className="font-bold pr-2 py-0.5 text-[#003366] whitespace-nowrap">NOMBRE:</td>
                  <td className="py-0.5 font-medium">{proveedor.nombre || oc.nombre_proveedor || ''}</td>
                </tr>
                <tr>
                  <td className="font-bold pr-2 py-0.5 text-[#003366] whitespace-nowrap">ATENCION:</td>
                  <td className="py-0.5">{proveedor.contacto || ''}</td>
                </tr>
                <tr>
                  <td className="font-bold pr-2 py-0.5 text-[#003366] whitespace-nowrap">GIRO:</td>
                  <td className="py-0.5">{proveedor.giro || ''}</td>
                </tr>
                <tr>
                  <td className="font-bold pr-2 py-0.5 text-[#003366] whitespace-nowrap">DIRECCION:</td>
                  <td className="py-0.5">{proveedor.direccion || ''}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="w-[200px]">
            <table className="text-[9px]">
              <tbody>
                <tr>
                  <td className="font-bold pr-2 py-0.5 text-[#003366] whitespace-nowrap">FECHA:</td>
                  <td className="py-0.5">{fmtFecha(oc.fecha_norm || oc.fecha)}</td>
                </tr>
                <tr>
                  <td className="font-bold pr-2 py-0.5 text-[#003366] whitespace-nowrap">RUT:</td>
                  <td className="py-0.5">{proveedor.rut || oc.rut_proveedor || ''}</td>
                </tr>
                <tr>
                  <td className="font-bold pr-2 py-0.5 text-[#003366] whitespace-nowrap">FONO:</td>
                  <td className="py-0.5">{proveedor.telefono || ''}</td>
                </tr>
                <tr>
                  <td className="font-bold pr-2 py-0.5 text-[#003366] whitespace-nowrap">CIUDAD:</td>
                  <td className="py-0.5">{proveedor.ciudad || proveedor.comuna || ''}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* === CONDICION DE PAGO + MEDIO DE PAGO (misma fila) === */}
      {(condPago || medioPagoNombre) && (
        <div className="px-3 py-1.5 border-b border-gray-300 flex items-center" data-testid="oc-forma-pago">
          <div className="flex-1 flex items-center gap-2">
            <span className="text-[9px] font-bold text-[#003366]">CONDICION DE PAGO:</span>
            <span className="text-[10px] font-semibold text-gray-700" data-testid="oc-viewer-condicion-pago">{condPago || '-'}</span>
          </div>
          {medioPagoNombre && (
            <div className="flex-1 flex items-center gap-2">
              <span className="text-[9px] font-bold text-[#003366]">MEDIO DE PAGO:</span>
              <span className="text-[10px] font-semibold text-gray-700" data-testid="oc-viewer-medio-pago">{medioPagoNombre}</span>
            </div>
          )}
        </div>
      )}

      {/* === ITEMS TABLE === */}
      <div className="border-b border-[#003366]">
        <table className="w-full">
          <thead>
            <tr className="bg-[#003366] text-white">
              <th className="px-2 py-1.5 text-center text-[8px] font-bold w-[35px]">ITEM</th>
              <th className="px-2 py-1.5 text-center text-[8px] font-bold w-[45px]">CANT</th>
              <th className="px-2 py-1.5 text-center text-[8px] font-bold w-[40px]">U.MED</th>
              <th className="px-2 py-1.5 text-left text-[8px] font-bold">DESCRIPCION</th>
              <th className="px-2 py-1.5 text-right text-[8px] font-bold w-[70px]">P.UNIT.</th>
              <th className="px-2 py-1.5 text-right text-[8px] font-bold w-[75px]">P.TOTAL</th>
              <th className="px-2 py-1.5 text-center text-[8px] font-bold w-[40px]">OT</th>
            </tr>
          </thead>
          <tbody>
            {detalle.length > 0 ? detalle.map((d, i) => (
              <tr key={i} className={`border-b border-gray-200 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <td className="px-2 py-1 text-center text-[9px]">{d.item || i + 1}</td>
                <td className="px-2 py-1 text-center text-[9px]">
                  {d.cantidad != null ? Number(d.cantidad).toLocaleString('es-CL', { maximumFractionDigits: 2 }) : ''}
                </td>
                <td className="px-2 py-1 text-center text-[9px]">{d.unidad || 'UN'}</td>
                <td className="px-2 py-1 text-[9px]">
                  <p className="font-medium text-gray-900">{d.descripcion}</p>
                  {d.observaciones && d.observaciones !== d.descripcion && (
                    <p className="text-[8px] text-blue-700 italic">{d.observaciones}</p>
                  )}
                  {d.codigo_material && <p className="text-[8px] text-gray-400">Cód: {d.codigo_material}</p>}
                </td>
                <td className="px-2 py-1 text-right text-[9px]">{fmt(d.precio_unitario || d.valor_unitario)}</td>
                <td className="px-2 py-1 text-right text-[9px] font-medium">{fmt(d.total_linea)}</td>
                <td className="px-2 py-1 text-center text-[9px] text-gray-500">{d.ot_numero || oc.ot_numero || ''}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400 text-[10px] italic">
                  Sin detalle de items disponible (pendiente importación de Access)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* === BOTTOM: OBSERVACION + OT · TOTALES === */}
      <div className="flex border-b border-[#003366]">
        <div className="flex-1 p-3 border-r border-gray-300">
          <div>
            <p className="text-[8px] font-bold text-[#003366] uppercase">OBSERVACION:</p>
            <p className="text-[9px] text-gray-700 min-h-[16px]">{oc.observaciones || '-'}</p>
            {oc.ot_resumen && (
              <p className="text-[9px] text-gray-800 mt-1" data-testid="oc-viewer-ot-resumen">
                <span className="font-bold text-[#003366]">OT:</span> {oc.ot_resumen}
              </p>
            )}
          </div>
        </div>

        {/* Totales — replica exacta del bloque _oc_pdf_totals_block */}
        <div className="w-[220px] p-3">
          <table className="w-full text-[10px]" data-testid="oc-viewer-totales">
            <tbody>
              <tr>
                <td className="font-bold text-[#003366] py-0.5">
                  {netoExento > 0 ? 'NETO AFECTO' : 'VALOR NETO'}
                </td>
                <td className="text-right py-0.5" data-testid="oc-viewer-neto">$ {fmt(netoExento > 0 ? netoAfecto : neto)}</td>
              </tr>
              {netoExento > 0 && (
                <tr>
                  <td className="font-bold text-[#003366] py-0.5">EXENTO</td>
                  <td className="text-right py-0.5" data-testid="oc-viewer-exento">$ {fmt(netoExento)}</td>
                </tr>
              )}
              {iva > 0 && (
                <tr>
                  <td className="font-bold text-[#003366] py-0.5">IVA {ivaPct}%</td>
                  <td className="text-right py-0.5">$ {fmt(iva)}</td>
                </tr>
              )}
              {otrosImpuestos.map((oi, i) => {
                const monto = Number(oi.monto || 0);
                if (!monto) return null;
                return (
                  <tr key={`oi-${i}`}>
                    <td className="font-bold text-[#003366] py-0.5 text-[8px]">{String(oi.nombre || 'Otro imp.').slice(0, 28)}</td>
                    <td className="text-right py-0.5">$ {fmt(monto)}</td>
                  </tr>
                );
              })}
              {retencionMonto > 0 && (
                <tr>
                  <td className="font-bold py-0.5 text-purple-700">Ret. {retencionPct}%</td>
                  <td className="text-right py-0.5 text-purple-700">-$ {fmt(retencionMonto)}</td>
                </tr>
              )}
              <tr className="border-t-2 border-[#003366]">
                <td className="font-black text-[#003366] py-1 text-[11px]">TOTAL</td>
                <td className="text-right font-black py-1 text-[11px]" data-testid="oc-viewer-total">$ {fmt(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* === WARNING BOX === */}
      <div className="mx-3 my-2 border border-amber-400 bg-amber-50 rounded px-3 py-1.5 flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
        <p className="text-[9px] font-bold text-amber-800">Incluir N de Orden de Compra en factura</p>
      </div>

      {/* === SIGNATURES — recuadros sin rotación, alineados al PDF === */}
      <div className="grid grid-cols-3 gap-4 px-4 py-3 border-t border-gray-300">
        {/* SOLICITADO POR / ADMINISTRADOR OBRA */}
        <div className="text-center">
          <div className="h-[55px] mb-1 flex items-center justify-center">
            {oc.solicitante && (
              <div className="inline-block border-2 border-[#003366] rounded px-3 py-1 bg-white" data-testid="stamp-admin-obra">
                <p className="text-[7px] font-bold text-[#003366] uppercase tracking-wider">Solicitado por</p>
                <p className="text-[10px] font-black text-[#003366]">{oc.solicitante}</p>
              </div>
            )}
          </div>
          <div className="border-b border-gray-400 mb-1"></div>
          <p className="text-[8px] font-bold text-[#003366]">ADMINISTRADOR OBRA</p>
        </div>

        {/* V°B° ADQUISICIONES */}
        <div className="text-center">
          <div className="h-[55px] mb-1 flex items-center justify-center">
            {(() => {
              const isFromAccess = !oc.creado_por && (!oc.source || oc.source === 'access');
              if (isFromAccess) {
                return (
                  <div className="inline-block border-2 border-gray-500 rounded px-3 py-1 bg-gray-50" data-testid="stamp-adquisiciones-access">
                    <p className="text-[8px] font-black text-gray-600 tracking-wider">MS ACCESS</p>
                    <p className="text-[7px] text-gray-500">Importado</p>
                  </div>
                );
              }
              return (
                <div className="inline-block relative" data-testid="stamp-adquisiciones">
                  <div className="border-[2.5px] border-[#1a6b3c] rounded-full w-[58px] h-[58px] flex flex-col items-center justify-center opacity-90"
                    style={{ boxShadow: 'inset 0 0 0 1.5px #1a6b3c' }}>
                    <p className="text-[6px] font-bold text-[#1a6b3c] uppercase leading-none">Adquisiciones</p>
                    <p className="text-[8px] font-black text-[#1a6b3c] leading-tight text-center max-w-[50px] break-words">
                      {creadorNombre.split(' ').slice(0, 2).join(' ')}
                    </p>
                    <p className="text-[5px] text-[#1a6b3c] leading-none mt-0.5">CHIRINO STEEL</p>
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="border-b border-gray-400 mb-1"></div>
          <p className="text-[8px] font-bold text-[#003366]">V°B° ADQUISICIONES</p>
        </div>

        {/* V°B° GERENCIA */}
        <div className="text-center">
          <div className="h-[55px] mb-1 flex items-center justify-center">
            {(oc.vb_gerencia || oc.aprobada) && (
              <div className="inline-block relative" data-testid="stamp-gerencia">
                <div className={`border-[2.5px] ${oc.vb_gerencia ? 'border-[#8b1a1a]' : 'border-gray-500'} rounded-full w-[58px] h-[58px] flex flex-col items-center justify-center opacity-90`}
                  style={{ boxShadow: `inset 0 0 0 1.5px ${oc.vb_gerencia ? '#8b1a1a' : '#6b7280'}` }}>
                  <p className={`text-[6px] font-bold uppercase leading-none ${oc.vb_gerencia ? 'text-[#8b1a1a]' : 'text-gray-500'}`}>Gerencia</p>
                  {(!oc.vb_gerencia && oc.aprobada) ? (
                    <p className="text-[7px] font-black text-gray-500 leading-tight">APROBADA</p>
                  ) : (
                    <p className="text-[8px] font-black text-[#8b1a1a] leading-tight text-center max-w-[50px] break-words">
                      {gerenteNombre.split(' ').slice(0, 2).join(' ')}
                    </p>
                  )}
                  <p className={`text-[5px] leading-none mt-0.5 ${oc.vb_gerencia ? 'text-[#8b1a1a]' : 'text-gray-500'}`}>CHIRINO STEEL</p>
                </div>
              </div>
            )}
          </div>
          <div className="border-b border-gray-400 mb-1"></div>
          <p className="text-[8px] font-bold text-[#003366]">V°B° GERENCIA</p>
        </div>
      </div>

      {/* === SELLO DE RECEPCIÓN (Iter 411) === */}
      <SelloRecepcionBox
        sello={sello}
        editable={editableSello}
        numeroOc={numeroOc}
        onUpdated={setSello}
        variant="viewer"
      />

      {/* === V°B° PARA PAGO (Iter 413) === */}
      <VbPagoBox
        vb={vbPago}
        editable={editableVbPago}
        numeroOc={numeroOc}
        onUpdated={(data) => { setVbPago(data); onVbPagoChange?.(numeroOc, data); }}
        variant="viewer"
      />

      {/* === FOOTER: Generado por + Generado el (mismo texto que el PDF) === */}
      <div className="px-4 py-2 text-center border-t border-gray-200" data-testid="oc-viewer-footer">
        {generadoPor && (
          <p className="text-[8px] text-gray-600 italic">Generado por: {generadoPor}</p>
        )}
        {generadoEl && (
          <p className="text-[8px] text-gray-500 italic">Generado el {fmtFechaHora(generadoEl)} · CHIRINO STEEL</p>
        )}
      </div>
    </div>
    </div>
  );
}
